import type {
  CompiledKMachine,
  KComplex,
  KExecutionState,
  TMState,
  TuringMachine,
} from '../engine/types'
import { readTape } from '../engine/turing'
import { DisclosureSection } from './DisclosureSection'
import { CurrentStepCard } from './MachinePanel'

interface KRuleDisplayProps {
  type: 'ku'
  machine: CompiledKMachine
  state: KExecutionState
}

interface TMRuleDisplayProps {
  type: 'tm'
  machine: TuringMachine
  state: TMState
}

type RuleDisplayProps = KRuleDisplayProps | TMRuleDisplayProps

function formatComplex(complex: KComplex): string[] {
  const vertices = complex.vertices
    .map((vertex) => `${vertex.id}:${vertex.type}`)
    .join(', ')
  const edges = complex.edges.length === 0
    ? 'none'
    : complex.edges.map((edge) => `${edge.a}—${edge.b}`).join(', ')
  return [`Vertices: ${vertices}`, `Edges: ${edges}`]
}

export function RuleDisplay(props: RuleDisplayProps) {
  if (props.type === 'tm') return <TMRuleDisplay machine={props.machine} state={props.state} />

  const { machine, state } = props
  const trace = state.lastTrace
  const rule = trace?.matchedRuleId
    ? machine.source.rules.find((candidate) => candidate.id === trace.matchedRuleId)
    : null
  const traversalCount = machine.source.rules.filter((candidate) =>
    candidate.tags?.includes('traversal'),
  ).length
  const finalCount = machine.source.rules.filter((candidate) =>
    candidate.tags?.includes('finalization'),
  ).length

  return (
    <div className="km-details-stack">
      <DisclosureSection
        disclosureId="ku-current-step"
        summary="Exact replacement trace"
        meta={rule?.title ?? (state.outcome === 'no-result' ? 'Undefined transition' : 'Initial state')}
      >
        {trace ? (
          <div className="km-rule-detail">
            <CurrentStepCard
              lines={[
                ['U', `${trace.partition.activeVertices.length} vertices`],
                ['L', `${trace.partition.frontierVertices.length} frontier vertices`],
                ['V', `${trace.partition.externalVertices.length} vertices`],
                ['Outcome', trace.outcome],
              ]}
            />
            <p>
              <strong>U:</strong> {trace.partition.activeVertices.join(', ')}
            </p>
            <p>
              <strong>L:</strong> {trace.partition.frontierVertices.join(', ') || 'empty'}
            </p>
            {rule ? (
              <>
                <p><strong>Matched:</strong> {rule.id}</p>
                <p>{rule.description}</p>
                <div className="km-rule-pair" aria-label="Matched replacement pair">
                  <div>
                    <h4>Uᵢ</h4>
                    {formatComplex(rule.input).map((line) => <code key={line}>{line}</code>)}
                  </div>
                  <div aria-hidden="true" className="km-rule-arrow">→</div>
                  <div>
                    <h4>Wᵢ</h4>
                    {formatComplex(rule.output).map((line) => <code key={line}>{line}</code>)}
                  </div>
                </div>
                <p>
                  <strong>Frontier attachment:</strong>{' '}
                  {rule.attachments
                    .map((attachment) => `${attachment.inputBoundary} ↔ ${attachment.outputVertex}`)
                    .join(', ')}
                </p>
                <p>
                  Removed {trace.removedVertices.length} interior vertices; created{' '}
                  {trace.createdVertices.length} fresh vertices. Frontier and external IDs persist.
                </p>
              </>
            ) : (
              <p>
                No Uᵢ has this rooted typed form. The transition operator is undefined here, so
                the computation stops without a result.
              </p>
            )}
          </div>
        ) : (
          <p className="km-detail-note">
            Step once to extract U, identify L, match a rule input, and inspect the replacement.
          </p>
        )}
      </DisclosureSection>

      <DisclosureSection
        disclosureId="ku-rulebook"
        summary="Generated finite rulebook"
        meta={`${machine.source.rules.length} exact pairs`}
      >
        <div className="km-rulebook-summary">
          <p>
            The compact program generator emits ordinary 1958-style pairs. Matching never uses
            these group labels; it uses the canonical rooted type and edge structure of Uᵢ.
          </p>
          <CurrentStepCard
            lines={[
              ['Traversal pairs', String(traversalCount)],
              ['Finalization pairs', String(finalCount)],
              ['Radius', String(machine.source.radius)],
              ['Profile', machine.source.model],
            ]}
          />
        </div>
      </DisclosureSection>
    </div>
  )
}

function TMRuleDisplay({ machine, state }: { machine: TuringMachine; state: TMState }) {
  const symbol = readTape(state, machine.blank)
  const transition = state.matchedTransition
  return (
    <div className="km-details-stack">
      <DisclosureSection
        disclosureId="tm-current-step"
        summary="Current transition"
        meta={transition ? `${transition.state}/${transition.read}` : state.outcome}
      >
        <CurrentStepCard
          lines={[
            ['State', state.state],
            ['Head', String(state.head)],
            ['Read', symbol === machine.blank ? 'blank' : symbol],
            ['Outcome', state.outcome],
          ]}
        />
        {transition ? (
          <p className="km-detail-note">
            <code>
              ({transition.state}, {transition.read}) → ({transition.write},{' '}
              {transition.move}, {transition.next})
            </code>
          </p>
        ) : (
          <p className="km-detail-note">
            {state.outcome === 'no-result'
              ? 'No transition is defined for this state and symbol.'
              : 'No transition has yet been applied.'}
          </p>
        )}
      </DisclosureSection>

      <DisclosureSection
        disclosureId="tm-transition-table"
        summary="Complete transition table"
        meta={`${machine.transitions.length} transitions`}
      >
        <div className="km-rulebook-summary">
          <p>
            The whole program, written for this one serialization. <code>{machine.blank}</code>{' '}
            is the blank symbol. The row matching the current state and read symbol is
            highlighted.
          </p>
          <div className="km-transition-table-wrap">
            <table className="km-transition-table">
              <thead>
                <tr>
                  <th scope="col">State</th>
                  <th scope="col">Read</th>
                  <th scope="col">Write</th>
                  <th scope="col">Move</th>
                  <th scope="col">Next</th>
                </tr>
              </thead>
              <tbody>
                {machine.transitions.map((candidate) => {
                  const current =
                    state.outcome === 'running'
                    && candidate.state === state.state
                    && candidate.read === symbol
                  return (
                    <tr
                      key={`${candidate.state}-${candidate.read}`}
                      className={current ? 'km-transition-current' : undefined}
                    >
                      <td>{candidate.state}</td>
                      <td>{candidate.read}</td>
                      <td>{candidate.write}</td>
                      <td>{candidate.move}</td>
                      <td>{candidate.next}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </DisclosureSection>
    </div>
  )
}
